import { useMemo, useState, useCallback } from 'react';
import { ValidationError } from './useFileUpload';

interface ErrorTableHook {
  processedErrors: Array<ValidationError & { id: string }>;
  totalErrorCount: number;
  errorsByCategory: Record<string, number>;
  currentPage: number;
  pageSize: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  sortErrors: (field: string, direction: 'ASC' | 'DESC') => void;
}

export function useErrorTable(errors: ValidationError[]): ErrorTableHook {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' } | null>(null);

  // Ensure errors are properly formatted and assign IDs
  const processedErrors = useMemo(() => {
    if (!Array.isArray(errors)) {
      console.error('Errors is not an array:', errors);
      return [];
    }
    
    const processed = errors.map((error, index) => {
      // Ensure error has all required fields or provide defaults
      if (!error || typeof error !== 'object') {
        console.warn(`Invalid error at index ${index}:`, error);
        return {
          id: `error-${index}`,
          sampleId: null,
          version: null,
          field: 'unknown',
          errorCode: 'UnknownError',
          message: 'An unknown error occurred'
        };
      }
      
      return {
        id: `error-${index}`, // Add id for table row key
        sampleId: error.sampleId ?? null,
        version: error.version ?? null,
        field: error.field || 'unknown',
        errorCode: error.errorCode || 'Error',
        message: error.message || 'No error message provided'
      };
    });

    // Apply sorting if configured
    if (sortConfig) {
      return [...processed].sort((a, b) => {
        const aValue = a[sortConfig.field as keyof typeof a];
        const bValue = b[sortConfig.field as keyof typeof b];
        
        if (aValue === null) return sortConfig.direction === 'ASC' ? -1 : 1;
        if (bValue === null) return sortConfig.direction === 'ASC' ? 1 : -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'ASC' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        // Default comparison for other types
        return sortConfig.direction === 'ASC' 
          ? (aValue < bValue ? -1 : 1) 
          : (aValue > bValue ? -1 : 1);
      });
    }
    
    return processed;
  }, [errors, sortConfig]);

  // Calculate total error count
  const totalErrorCount = processedErrors.length;

  // Calculate errors by category (field)
  const errorsByCategory = useMemo(() => {
    const categories: Record<string, number> = {};
    
    processedErrors.forEach(error => {
      const category = error.field;
      categories[category] = (categories[category] || 0) + 1;
    });
    
    return categories;
  }, [processedErrors]);

  // Sort errors based on field and direction
  const sortErrors = useCallback((field: string, direction: 'ASC' | 'DESC') => {
    setSortConfig({ field, direction });
    setCurrentPage(0); // Reset to first page when sorting
  }, []);

  return {
    processedErrors,
    totalErrorCount,
    errorsByCategory,
    currentPage,
    pageSize,
    setCurrentPage,
    setPageSize,
    sortErrors
  };
} 